import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { communicationsService, clientsService } from '../../services';
import CommunicationLogger from './CommunicationLogger';
import './CommunicationsHistory.css';

const CommunicationsHistory = () => {
  const { user } = useAuth();
  const [communications, setCommunications] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedClient, setSelectedClient] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCommunication, setEditingCommunication] = useState(null);

  useEffect(() => {
    fetchCommunications();
    fetchStats();
    fetchClients();
  }, [selectedFilter, selectedClient]);

  const fetchCommunications = async () => {
    try {
      setLoading(true);
      const filters = {};
      
      if (selectedClient) filters.client_id = selectedClient;
      if (selectedFilter === 'email') filters.communication_type = 'email';
      if (selectedFilter === 'phone') filters.communication_type = 'phone';
      if (selectedFilter === 'meeting') filters.communication_type = 'meeting';
      if (selectedFilter === 'inbound') filters.direction = 'inbound';
      if (selectedFilter === 'outbound') filters.direction = 'outbound';
      if (selectedFilter === 'follow_up') filters.follow_up_required = 'true';

      const data = await communicationsService.getCommunications(filters);
      setCommunications(data);
    } catch (error) {
      console.error('Failed to fetch communications:', error);
      setError('Failed to load communications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const statsData = await communicationsService.getCommunicationStats();
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch communication statistics:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const clientsData = await clientsService.getAll();
      setClients(clientsData);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const handleCreateCommunication = async (communicationData) => {
    try {
      await communicationsService.createCommunication(communicationData);
      setSuccess('Communication recorded successfully');
      await fetchCommunications();
      await fetchStats();
      setShowCreateModal(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to create communication:', error);
      setError('Failed to record communication. Please try again.');
    }
  };

  const handleUpdateCommunication = async (communicationId, updates) => {
    try {
      await communicationsService.updateCommunication(communicationId, updates);
      setSuccess('Communication updated successfully');
      await fetchCommunications();
      await fetchStats();
      setEditingCommunication(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to update communication:', error);
      setError('Failed to update communication. Please try again.');
    }
  };

  const handleDeleteCommunication = async (communicationId) => {
    if (!window.confirm('Are you sure you want to delete this communication record?')) {
      return;
    }

    try {
      await communicationsService.deleteCommunication(communicationId);
      setSuccess('Communication deleted successfully');
      await fetchCommunications();
      await fetchStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to delete communication:', error);
      setError('Failed to delete communication. Please try again.');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return '📧';
      case 'phone': return '📞';
      case 'meeting': return '🤝';
      case 'text': return '💬';
      case 'quote_message': return '💼';
      default: return '📝';
    }
  };

  const getDirectionColor = (direction) => {
    return direction === 'inbound' ? 'text-blue-600 bg-blue-100' : 'text-green-600 bg-green-100';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (loading && communications.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading communications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Communication History</h2>
          <p className="mt-1 text-sm text-gray-600">
            Track all client communications and follow-ups
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Record Communication
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-sm">{stats.total_communications || 0}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Communications</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.total_communications || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-semibold text-sm">{stats.clients_communicated_with || 0}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Clients Contacted</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.clients_communicated_with || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-yellow-600 font-semibold text-sm">{stats.pending_followups || 0}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Pending Follow-ups</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.pending_followups || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-red-600 font-semibold text-sm">{stats.overdue_followups || 0}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Overdue Follow-ups</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.overdue_followups || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Clients</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.first_name} {client.last_name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { key: 'all', label: 'All', count: stats.total_communications },
              { key: 'email', label: 'Email', count: stats.email_count },
              { key: 'phone', label: 'Phone', count: stats.phone_count },
              { key: 'meeting', label: 'Meetings', count: stats.meeting_count },
              { key: 'quote_message', label: 'Quote Messages', count: stats.quote_message_count },
              { key: 'inbound', label: 'Inbound', count: stats.inbound_count },
              { key: 'outbound', label: 'Outbound', count: stats.outbound_count },
              { key: 'follow_up', label: 'Follow-ups', count: stats.pending_followups }
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setSelectedFilter(filter.key)}
                className={`${
                  selectedFilter === filter.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
              >
                {filter.label}
                {filter.count > 0 && (
                  <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    selectedFilter === filter.key ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {filter.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Communications List */}
      {communications.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No communications found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {selectedFilter === 'all' && !selectedClient
              ? 'Start by recording your first client communication.'
              : 'No communications match your current filters.'}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {communications.map((comm) => (
              <li key={comm.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{getTypeIcon(comm.communication_type)}</span>
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            {comm.subject || (comm.communication_type === 'quote_message' && comm.quote_number 
                              ? `Quote Message - ${comm.quote_number}${comm.quote_title ? ` (${comm.quote_title})` : ''}`
                              : `${comm.communication_type.charAt(0).toUpperCase() + comm.communication_type.slice(1)} with ${comm.client_first_name} ${comm.client_last_name}`)}
                          </h3>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDirectionColor(comm.direction)}`}>
                              {comm.direction}
                            </span>
                            <span className="text-sm text-gray-500">
                              {formatDate(comm.communication_date)}
                            </span>
                            {comm.duration_minutes && (
                              <span className="text-sm text-gray-500">
                                Duration: {formatDuration(comm.duration_minutes)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-2 flex items-center text-sm text-gray-500 space-x-4">
                        {comm.communication_type === 'quote_message' && comm.quote_number ? (
                          <span>
                            <strong>Quote:</strong> {comm.quote_number} - {comm.client_name || 'Unknown Client'}
                          </span>
                        ) : (
                          <span>
                            <strong>Client:</strong> {comm.client_first_name} {comm.client_last_name}
                          </span>
                        )}
                        {comm.workflow_stage_name && (
                          <span>
                            <strong>Stage:</strong> {comm.workflow_stage_name}
                          </span>
                        )}
                        {comm.related_task_title && (
                          <span>
                            <strong>Related Task:</strong> {comm.related_task_title}
                          </span>
                        )}
                      </div>

                      {comm.content && (
                        <p className="mt-2 text-sm text-gray-600 line-clamp-3">{comm.content}</p>
                      )}

                      {comm.follow_up_required && (
                        <div className="mt-2 flex items-center text-sm">
                          <span className="text-yellow-600 font-medium">Follow-up required</span>
                          {comm.follow_up_date && (
                            <span className="ml-2 text-gray-500">
                              by {formatDate(comm.follow_up_date)}
                            </span>
                          )}
                        </div>
                      )}

                      {comm.tags && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {comm.tags.split(',').map((tag, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {tag.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setEditingCommunication(comm)}
                        className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteCommunication(comm.id)}
                        className="text-red-600 hover:text-red-900 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Communication Logger Modal - Create */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <CommunicationLogger
              onClose={() => setShowCreateModal(false)}
              onSaved={() => {
                fetchCommunications();
                fetchStats();
                setShowCreateModal(false);
                setSuccess('Communication recorded successfully');
                setTimeout(() => setSuccess(null), 3000);
              }}
            />
          </div>
        </div>
      )}

      {/* Communication Logger Modal - Edit */}
      {editingCommunication && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <CommunicationLogger
              communication={editingCommunication}
              onClose={() => setEditingCommunication(null)}
              onSaved={() => {
                fetchCommunications();
                fetchStats();
                setEditingCommunication(null);
                setSuccess('Communication updated successfully');
                setTimeout(() => setSuccess(null), 3000);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationsHistory;